import React, { useState, useEffect } from "react";
import {
  Globe,
  Instagram,
  CalendarHeart,
  Syringe,
  EllipsisVertical,
  Check,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "./ui/dropdown-menu";
import "./BioLink.css";

const BG_IMAGE =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_imperium-basel/artifacts/941q58uo_WhatsApp%20Image%202026-09-18%20at%2018.32.49.jpeg";
const PROFILE_IMAGE =
  "https://customer-assets-v7afamib.emergentagent.net/job_imperium-wellness/artifacts/r3rm3tfi_WhatsApp%20Image%202026-09-18%20at%2020.18.43.jpeg";

// Available languages for the globe selector
const LANGS = [
  { code: "de", short: "DE", label: "Deutsch" },
  { code: "it", short: "IT", label: "Italiano" },
  { code: "fr", short: "FR", label: "Fran\u00e7ais" },
  { code: "pt", short: "PT", label: "Portugu\u00eas (Portugal)" },
  { code: "br", short: "BR", label: "Portugu\u00eas (Brasil)" },
];

// Translations for the visible, translatable texts.
// Proper nouns (brand name, address, phone, email, copyright) are NOT translated.
const T = {
  de: {
    tagline: "Entdecken Sie die Leichtigkeit, sich zu verw\u00f6hnen",
    agendar: "Termin vereinbaren",
    imersao: "Injektions-Immersion",
    visite: "Besuchen Sie unsere Website",
  },
  it: {
    tagline: "Scopri la leggerezza di prendersi cura di s\u00e9",
    agendar: "Prenota una consulenza",
    imersao: "Immersione negli Iniettabili",
    visite: "Visita il nostro sito",
  },
  fr: {
    tagline: "D\u00e9couvrez la l\u00e9g\u00e8ret\u00e9 de prendre soin de soi",
    agendar: "Prendre rendez-vous",
    imersao: "Immersion en Injectables",
    visite: "Visitez notre site",
  },
  pt: {
    tagline: "Descubra a leveza de se cuidar",
    agendar: "Marcar consulta",
    imersao: "Imers\u00e3o em Injet\u00e1veis",
    visite: "Visite o nosso site",
  },
  br: {
    tagline: "Descubra a leveza de se cuidar",
    agendar: "Agendar consulta",
    imersao: "Imers\u00e3o em Injet\u00e1veis",
    visite: "Visite o nosso site",
  },
};

// Links must NOT change regardless of the selected language.
const LINKS = [
  {
    key: "agendar",
    Icon: CalendarHeart,
    href:
      "https://wa.me/41798463637?text=Ol%C3%A1!%20Vim%20pelo%20Instagram%20da%20Imperium%20Beauty%20Wellness%20e%20gostaria%20de%20agendar%20uma%20consulta.",
  },
  {
    key: "imersao",
    Icon: Syringe,
    href:
      "https://wa.me/41798463637?text=Ol%C3%A1!%20Vim%20pelo%20Instagram%20da%20Imperium%20Beauty%20Wellness%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es%20sobre%20Imers%C3%A3o%20em%20Injet%C3%A1veis.",
  },
  {
    key: "visite",
    Icon: Globe,
    href: null, // resolved to the matching language of the main website
  },
];

const SITE_LANG = {
  de: "de",
  it: "it",
  fr: "fr",
  pt: "pt-pt",
  br: "pt-br",
};

const BioLink = () => {
  const [lang, setLang] = useState("de");

  useEffect(() => {
    document.title = "Imperium Beauty Wellness | Links";
  }, []);

  const handleSelect = (code) => {
    setLang(code);
  };

  const current = LANGS.find((l) => l.code === lang) || LANGS[0];
  const t = T[lang];

  return (
    <div className="ibw-page">
      <div
        className="ibw-bg"
        style={{ backgroundImage: `url("${BG_IMAGE}")` }}
      />
      <div className="ibw-bg-overlay" />

      {/* Language selector */}
      <div className="ibw-lang">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="ibw-lang-btn" aria-label="Selecionar idioma">
              <Globe size={16} strokeWidth={1.9} />
              <span className="ibw-lang-code">{current.short}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="ibw-lang-menu">
            {LANGS.map((l) => (
              <DropdownMenuItem
                key={l.code}
                className="ibw-lang-item"
                onClick={() => handleSelect(l.code)}
              >
                <span>{l.label}</span>
                {l.code === lang && <Check size={15} strokeWidth={2} />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="ibw-container">
        <div className="ibw-photo-wrap">
          <img
            className="ibw-photo"
            alt="Imperium Beauty Wellness"
            src={PROFILE_IMAGE}
          />
        </div>

        <h1 className="ibw-username">imperiumbasel</h1>
        <p className="ibw-brand">Imperium Beauty Wellness</p>
        <p className="ibw-tagline">{t.tagline}</p>

        <a
          className="ibw-ig"
          href="https://www.instagram.com/imperiumbasel/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Instagram"
        >
          <Instagram size={17} strokeWidth={1.8} />
        </a>

        <div className="ibw-buttons">
          {LINKS.map((link) => {
            const Icon = link.Icon;
            const isWebsite = link.key === "visite";
            const href = isWebsite ? `/${SITE_LANG[lang] || "de"}` : link.href;
            return (
              <a
                key={link.key}
                className="ibw-btn"
                href={href}
                target={isWebsite ? undefined : "_blank"}
                rel={isWebsite ? undefined : "noopener noreferrer"}
              >
                <span className="ibw-btn-icon">
                  <Icon size={15} strokeWidth={1.8} />
                </span>
                <span className="ibw-btn-label">{t[link.key]}</span>
                <span className="ibw-btn-dots">
                  <EllipsisVertical size={16} strokeWidth={2} />
                </span>
              </a>
            );
          })}
        </div>

        <div className="ibw-footer">
          <p className="ibw-footer-name">Imperium Beauty Wellness</p>
          <p className="ibw-footer-line">
            Austrasse 31, 4051 Basel
            <br />
            +41 79 846 36 37
            <br />
            kontakt@imperium-wellness.ch
          </p>
          <p className="ibw-footer-copy">{"\u00a9"} 2026 Imperium Beauty Wellness</p>
        </div>
      </div>
    </div>
  );
};

export default BioLink;
